import useApi, {
  fetcher,
  doFetch,
} from "@action.sustainability/storybook-dashboard/utils/fetching";

import {
  useForm,
  useFieldArray,
  useController,
  useWatch,
} from "react-hook-form";

import TipsSpinner from "@action.sustainability/storybook-dashboard/spinners/tipsSpinner";
import ChartJSWrapper from "@action.sustainability/storybook-dashboard/charts/chartjs";
import { chartColors } from "@action.sustainability/storybook-dashboard/dashboard/charts/utils";

import { useHistory, useParams } from "react-router-dom";

import React, { useEffect, useState } from "react";
import Octicon from "react-octicon";
import FileSaver from "file-saver";

import { calculateFields } from "./calculations";
import { CCP_API_URL } from "../fetching";
import FileDropZone from "../dragDropUpload";
import exportToXLS from "../catalog/exportToXLS";

import {
  a1Section,
  a2Section,
  a3Section,
  a4Section,
  b8Section,
  metaSection,
  RedStar,
} from "./form";

export default function AssetForm(props) {
  const [submitModal, setSubmitModal] = useState(false);
  const toggleSubmitModal = () => setSubmitModal(!submitModal);

  const router = useHistory();
  const { type, id, companyId } = useParams();

  const { trigger: triggerProductList } = useApi(
    `${CCP_API_URL}/${companyId}/product/product/`,
  );
  const { trigger: triggerDraftList } = useApi(
    `${CCP_API_URL}/${companyId}/product/draft/`,
  );

  const {
    data: _imData,
    isLoading,
    trigger: triggerAssetDetails,
  } = useApi(`${CCP_API_URL}/${companyId}/product/${type}/${id}`);

  const isDraft = () => {
    return type == "draft";
  };

  const { register, control, handleSubmit, reset, getValues, watch } = useForm(
    {},
  );

  const { fields: metafields, component: metaComponent } = metaSection({
    control,
    register,
    watch,
  });

  const { fields: a1fields, component: a1Component } = a1Section({
    control,
    register,
    watch,
    isDraft: isDraft(),
  });

  const { fields: a2fields, component: a2Component } = a2Section({
    control,
    register,
    watch,
    a1fields,
    isDraft: isDraft(),
  });

  const { fields: a3fields, component: a3Component } = a3Section({
    control,
    register,
    watch,
    isDraft: isDraft(),
  });

  const { fields: a4fields, component: a4Component } = a4Section({
    control,
    register,
    watch,
    isDraft: isDraft(),
  });

  const { fields: b8fields, component: b8Component } = b8Section({
    control,
    register,
    watch,
  });

  useEffect(() => {
    const downloadedData = _imData?.toJS();
    reset({ ...downloadedData });
    try {
      if (downloadedData) {
        const {
          totals,
          details: { mass },
        } = calculateFields({ data: downloadedData });
        setTotals({ mass, ...totals });
      }
    } catch (e) {}
  }, [_imData]);

  const saveData = async () => {
    // Only Drafts can be updated
    if (!isDraft()) {
      return;
    }
    const data = getValues();
    console.log("AssetForm saving data", data);
    const response = await fetcher(
      `${CCP_API_URL}/${companyId}/product/draft/${id}`,
      "PATCH",
      data,
    );
    await triggerAssetDetails();
    await triggerDraftList();
  };

  const saveAndReturn = () => {
    saveData();
    router.push(`/company/${companyId}/catalog`);
  };

  const submitProduct = async () => {
    if (!isDraft()) {
      return;
    } // Only Drafts can be turned into products
    await saveData();
    const response = await fetcher(
      `${CCP_API_URL}/${companyId}/product/product/`,
      "POST",
      {
        draft_id: id,
      },
    );
    await triggerDraftList();
    await triggerProductList();
    router.push(`/company/${companyId}/catalog`);
  };

  const exportData = async (e) => {
    e.preventDefault();
    const workbook = exportToXLS({ data: [getValues()] });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.ms-excel",
    });
    FileSaver.saveAs(blob, "PCC asset data.xlsx");
  };

  if (isLoading) {
    return (
      <div className="container">
        <TipsSpinner />;
      </div>
    );
  }

  const EmissionsBreakdown = () => {
    try {
      const { totals } = calculateFields({
        data: {
          ...useWatch({ control }),
          ...getValues(),
        },
      });

      return (
        <div
          className="shadow p-1 bg-white rounded"
          style={{ cursor: "pointer" }}
        >
          <h6 className="pl-3 py-1 mb-0">Asset Emission Makeup</h6>
          <ChartJSWrapper
            chartData={{
              type: "doughnut",
              data: {
                labels: [
                  "A1 (kgCO2e)",
                  "A2 (kgCO2e)",
                  "A3 (kgCO2e)",
                  "A4 (kgCO2e)",
                ],
                datasets: [
                  {
                    label: "Emissions",
                    data: [totals.a1, totals.a2, totals.a3, totals.a4],
                    hoverOffset: 2,
                    backgroundColor: [
                      "#AE8CA3",
                      "#90A9B7",
                      "#FF5964",
                      "#A3A380",
                    ],
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                cutoutPercentage: 0,
                plugins: {
                  legend: {
                    display: true,
                  },
                  tooltip: {
                    bodyFont: { size: 11 },
                  },
                },
              },
            }}
          />
        </div>
      );
    } catch {
      return <></>;
    }
  };

  const MassAllocation = () => {
    try {
      const data = {
        ...useWatch({ control }),
        ...getValues(),
      };

      const { totals } = calculateFields({ data });
      return (
        <div
          className="shadow p-1 bg-white rounded"
          style={{ cursor: "pointer" }}
        >
          <h6 className="pl-3 py-1 mb-0">Asset Completeness</h6>
          <ChartJSWrapper
            chartData={{
              type: "doughnut",
              data: {
                labels: ["Assigned (kg)", "Unassigned (kg)"],
                datasets: [
                  {
                    label: "Mass",
                    data: [
                      totals.assigned_mass,
                      data.details.mass - totals.assigned_mass,
                    ],
                    hoverOffset: 2,
                    backgroundColor: ["#F4A5AE", "#0A210F"],
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                cutoutPercentage: 0,
                plugins: {
                  legend: {
                    display: true,
                  },
                  tooltip: {
                    bodyFont: { size: 11 },
                  },
                },
              },
            }}
          />
        </div>
      );
    } catch {
      return <></>;
    }
  };

  return (
    <div className="container mt-5 ">
      <button className="btn btn-link text-muted" onClick={saveAndReturn}>
        <p
          className="text-muted text-underline mb-5"
          style={{ cursor: "pointer", textDecoration: "underline" }}
        >
          <Octicon name="chevron-left" /> Save and return
        </p>
      </button>
      <h4 className="mb-4 font-weight-bold">Asset details</h4>

      <form>
        <fieldset disabled={!isDraft()}>
          {metaComponent}
          <EmissionsBreakdown />
          <MassAllocation />
          <FileDropZone draftId={id} companyId={companyId} type={type} />{" "}
          <p className="text-muted my-2">
            Please upload any supporting documents, such as Environmental
            Product Declarations (EPDs), Product Environmental Profiles (PEPs),
            or other relevant information that supports your submission.
          </p>
          <h4 className="mt-5 mb-1 font-weight-bold">
            Embodied carbon calculations
          </h4>
          {isDraft() && (
            <p className="text-muted my-2">
              Please use the Add new entry button to add a new entry to each
              section. Only fields with <RedStar /> are mandatory.
            </p>
          )}
          {a1Component}
          {a2Component}
          {a3Component}
          {a4Component}
          <h4 className="mt-5 mb-1 font-weight-bold">In use emissions</h4>
          <p className="text-muted mt-2 mb-3">
            Applicable for assets with guaranteed insulating gas leakage during
            normal operations
          </p>
          {b8Component}
          {isDraft() && (
            <button
              type="button"
              className="btn btn-outline-primary my-5 mr-2"
              onClick={saveAndReturn}
            >
              Save draft and return to catalog
            </button>
          )}
          {isDraft() && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit(toggleSubmitModal)}
            >
              Submit
            </button>
          )}
        </fieldset>
        {!isDraft() && (
          <button className="btn btn-primary" onClick={exportData}>
            Export data
          </button>
        )}
      </form>

      {/* Submission Confirmation Modal */}
      <div
        className={`modal fade ${submitModal ? "show" : ""}`}
        style={{ display: submitModal ? "flex" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered justify-content-center"
          role="document"
        >
          <div className="modal-content ">
            <div className="modal-header border-0 text-center">
              <h4 className="modal-title font-weight-bold mt-3 w-100">
                Confirm submission
              </h4>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={toggleSubmitModal}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body text-center font-weight-bold">
              <p>
                Are you certain you wish to submit this asset to your catalogue?
              </p>
              <p>
                Please note, once submitted, you can share it with clients but
                will not be able to make further edits.
              </p>
              <br />
            </div>
            <div className="modal-footer border-0">
              <button
                type="button"
                className="btn btn-link"
                data-dismiss="modal"
                onClick={toggleSubmitModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  submitProduct();
                  toggleSubmitModal();
                }}
              >
                Yes, submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {submitModal && <div className="modal-backdrop fade show" />}
    </div>
  );
}
